import { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import LoadingScreen from '../components/commonComponents/LoadingScreen';
import DashboardLayout from '../layouts/dashboard';
import SidebarConfig from '../layouts/dashboard/SidebarConfig';
import SidebarConfigBasicData from '../layouts/dashboard/SidebarConfigBasicData';
import SidebarConfigConfigurations from '../layouts/dashboard/SidebarConfigConfigurations';
import SidebarConfigHam from '../layouts/dashboard/SidebarConfigHam';
import IdmEmailTemplatePage from '../pages/configurations/IdmEmailTemplatePage';
import { AppState } from '../redux/rootReducerTypes';
import { getIdentityClientSettings } from '../utils/localStorage';
import useEmbedded from '../hooks/useEmbedded';
import AdminPage from '../pages/generalPages/AdminPage';
import { AuthMethod } from '../common/enums';
// ----------------------------------------------------------------------

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

const Profile = () => {
  const identityClientSettings = getIdentityClientSettings();

  useEffect(() => {
    window.location.href = `${identityClientSettings.authority}/Manage/EditProfile`;
  }, []);

  return (<></>);
};

const ChangePassword = () => {
  const identityClientSettings = getIdentityClientSettings();

  useEffect(() => {
    window.location.href = `${identityClientSettings.authority}/Manage/Security`;
  }, []);

  return (<></>);
};

export default function Router() {
  const user = useSelector((state: AppState) => state.userState.user);
  const selectedClient = localStorage.getItem('selectedClient');
  const authMethod = getIdentityClientSettings().authMethod;
  const userInformation = useSelector((state: AppState) => state.userInformationState.userInformation);
  const isEmbedded = useEmbedded();
  const embeddedComponentBasePath = localStorage.getItem("component");
  let embeddedComponentRoute: string | null = null;

  if (isEmbedded && embeddedComponentBasePath) {
    embeddedComponentRoute = `/${embeddedComponentBasePath}`;
  }

  const validateUserAndClient = (component: JSX.Element, user: any, selectedClient: string | null) => {
    //AzureB2C employee users dont have backend/client assignments list, so they manually need to enter their Client Id
    // if no client is selected yet, send to admin page
    if (!selectedClient && authMethod === AuthMethod.AzureB2C) {
      return <Navigate to="/admin" replace />;
    }
    // if there's no authenticated user, send to login
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    // both user and client exist — allow rendering
    return component;
  }

  return useRoutes([
    // Main Routes     
    {
      path: '/',
      element: validateUserAndClient(renderUserRoute(userInformation, '/app'), user, selectedClient),
    },
    {
      path: '/app',
      element:
        embeddedComponentRoute ? <Navigate to={`/${embeddedComponentBasePath}`} replace /> :
          validateUserAndClient(renderUserRoute(userInformation, '/dashboard'), user, selectedClient),
      children: [{ path: '', element: <Dashboard /> }]
    },
    {
      path: '/admin',
      element: <AdminPage />
    },
    {
      path: '/ham',
      element:
        validateUserAndClient(renderUserRoute(userInformation, '/ham'), user, selectedClient),
      children: [
        { path: '', element: <DashboardHamPage /> },
        { path: 'Dashboard', element: <DashboardHamPage /> },
        { path: 'Entries', element: <HoursAndCostsPage /> },
        { path: 'Settings', element: <HamSettings /> },
        { path: 'HamApproval', element: <HamApproval /> },
        { path: 'OrderApproval', element: <OrderApproval /> },
      ]
    },
    {
      path: '/configurations',
      element:
        validateUserAndClient(renderUserRoute(userInformation, '/configurations'), user, selectedClient),
      children: [
        { path: '', element: <ActivityNotification /> },
        { path: 'Notification', element: <ActivityNotification /> },
        { path: 'IdmEmailTemplate', element: <IdmEmailTemplatePage /> }
      ]
    },
    {
      path: '/basicdata',
      element:
        validateUserAndClient(renderUserRoute(userInformation, '/basicdata'), user, selectedClient),
      children: [
        { path: '', element: <FileImportPage /> },
        { path: 'FileImport', element: <FileImportPage /> },
        { path: 'QuoteEquipment', element: <QuoteEquipmentPage /> }
      ]
    },
    {
      path: '/discrepancy',
      element:
        validateUserAndClient(renderUserRoute(userInformation, '/discrepancy'), user, selectedClient),
      children: [
        { path: '', element: <DiscrepancyListPage /> }
      ]
    },
    {
      path: '/discrepancyDetails',
      element:
        validateUserAndClient(renderUserRoute(userInformation, '/discrepancy'), user, selectedClient),
      children: [
        { path: '', element: <DiscrepancyDetails /> }
      ]
    },
    {
      path: '/login',
      element: <Login />
    },
    {
      path: '/oauth-callback',
      element: <LoginCallback />
    },
    {
      path: '/logout',
      element: <Logout />
    },
    {
      path: '/silent-renew',
      element: <SilentRenew />
    },
    {
      path: '/profile',
      element: <Profile />
    },
    {
      path: '/change-password',
      element: <ChangePassword />
    },
    {
      path: '/access-denied',
      element: <AccessDeniedPage />
    },
    {
      path: '*',
      element: <Navigate to="/not-found" replace />
    }
  ]);
}

// Dashboard
const Dashboard = Loadable(lazy(() => import('../pages/dashboard/DashboardPage')));
const HamApproval = Loadable(lazy(() => import('../components/HamComponents/hamV2/HamV2')));
const OrderApproval = Loadable(lazy(() => import('../pages/order/OrderApproval')));

// HAM
const HamSettings = Loadable(lazy(() => import('../pages/ham/HamSettingsPage')));
const HoursAndCostsPage = Loadable(lazy(() => import('../pages/hours-and-costs/HoursAndCostsPage')));
const DashboardHamPage = Loadable(lazy(() => import('../pages/hours-and-costs/DashboardHamPage')));

// Settings
const ActivityNotification = Loadable(lazy(() => import('../pages/configurations/ActivityNotificationPage')));

// Basic Data
const FileImportPage = Loadable(lazy(() => import('../pages/basicdata/FileImportPage')));
const QuoteEquipmentPage = Loadable(lazy(() => import('../pages/basicdata/QuoteEquipmentPage')));
//Discrepancy
const DiscrepancyListPage = Loadable(lazy(() => import('../components/discrepancyComponents/DiscrepancyListPage')));
const DiscrepancyDetails = Loadable(lazy(() => import('../components/discrepancyComponents/details/DiscrepancyDetails')));
//General pages
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));

const LoginCallback = Loadable(lazy(() => import('../pages/authentication/LoginCallback')));

const Logout = Loadable(lazy(() => import('../pages/authentication/Logout')));

const SilentRenew = Loadable(lazy(() => import('../pages/authentication/SilentRenew')));

const AccessDeniedPage = Loadable(lazy(() => import('../pages/generalPages/AccessDeniedPage')));

const routeConfig: Record<string, { component: JSX.Element }> = {
  '/app': { component: <Navigate to="/app" replace /> },
  '/dashboard': { component: <DashboardLayout navConfig={SidebarConfig} /> },
  '/ham': { component: <DashboardLayout navConfig={SidebarConfigHam} /> },
  '/configurations': { component: <DashboardLayout navConfig={SidebarConfigConfigurations} /> },
  '/basicdata': { component: <DashboardLayout navConfig={SidebarConfigBasicData} /> },
  '/discrepancy': { component: <DashboardLayout navConfig={SidebarConfig} /> },
};

function hasAccessToWebOfficeConfigurationPages(user: any, routeType: string): boolean {
  const isAzureB2CMethod = getIdentityClientSettings().authMethod === AuthMethod.AzureB2C;
  const isValidEmployee = user?.employeeId !== undefined && user.employeeId !== null;
  const isClientOwner = user?.isClientOwner !== undefined && user.isClientOwner === true;
  const isSystemAdmin = user?.isHandymanSystemAdministrator !== undefined && user.isHandymanSystemAdministrator === true;


    if (isAzureB2CMethod && (routeType === '/configurations' || routeType === '/basicdata' || routeType === '/' || routeType === '/app')) {
    return isClientOwner || isSystemAdmin;
  }

  return isValidEmployee;
}

export function renderUserRoute(user: any, routeType: string): JSX.Element {
  const route = routeConfig[routeType];

  if (!route) {
    return <Navigate to="/access-denied" replace />;
  }

  if (!hasAccessToWebOfficeConfigurationPages(user, routeType)) {
    return <Navigate to="/access-denied" replace />;
  }

  return route.component;
}